/**
 * Resolução de rotas e decisões de acesso por role (RBAC frontend).
 * Funções puras — usadas por login, ProtectedRoute e gating de fetch admin.
 */

export type AuthRole = 'ADMIN' | 'DOCTOR' | 'PATIENT';

export type ProtectedRouteDecision =
  | { action: 'loading' }
  | { action: 'login' }
  | { action: 'forbidden'; fallbackRoute: string; userRole: string; requiredRoles: string[] }
  | { action: 'allow'; userRole: string };

export function normalizeAuthRole(role?: string | null): string {
  return String(role || '').trim().toUpperCase();
}

/** Destino inicial pós-login / pós-negação de rota, por role. */
export function getDefaultRouteByRole(role?: string | null): string {
  const normalized = normalizeAuthRole(role);

  switch (normalized) {
    case 'ADMIN':
      // Painel administrativo (stats + overview). Rotas /admin/* são gestão.
      return '/dashboard';
    case 'DOCTOR':
      return '/dashboard';
    case 'PATIENT':
      return '/dashboard';
    default:
      return '/dashboard';
  }
}

export function isAdminRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'ADMIN';
}

export function isDoctorRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'DOCTOR';
}

export function isPatientRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'PATIENT';
}

/** Gating de fetch: nunca disparar /api/admin/* sem role ADMIN confirmada. */
export function shouldFetchAdminResources(
  role?: string | null,
  isAuthLoading = false,
): boolean {
  if (isAuthLoading) return false;
  return isAdminRole(role);
}

/**
 * Decide se ProtectedRoute deve aguardar, redirecionar ou renderizar children.
 * Evita render de páginas admin antes da role estar disponível.
 */
export function resolveProtectedRouteDecision(params: {
  token: string | null | undefined;
  userRole?: string | null;
  requiredRoles?: string[];
  isAuthLoading?: boolean;
}): ProtectedRouteDecision {
  const { token, requiredRoles, isAuthLoading = false } = params;
  const userRole = normalizeAuthRole(params.userRole);

  if (isAuthLoading) {
    return { action: 'loading' };
  }

  if (!token) {
    return { action: 'login' };
  }

  // Sem role ainda e auth carregando: aguardar (não renderizar children admin)
  if (requiredRoles && requiredRoles.length > 0 && !userRole) {
    if (isAuthLoading) {
      return { action: 'loading' };
    }
    // Token presente mas role ausente (sessão inconsistente) → home segura
    return {
      action: 'forbidden',
      fallbackRoute: getDefaultRouteByRole(null),
      userRole: '(vazio)',
      requiredRoles: requiredRoles.map((r) => normalizeAuthRole(r)),
    };
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const allowed = requiredRoles.some(
      (role) => normalizeAuthRole(role) === userRole,
    );

    if (!allowed) {
      return {
        action: 'forbidden',
        fallbackRoute: getDefaultRouteByRole(userRole),
        userRole: userRole || '(vazio)',
        requiredRoles: requiredRoles.map((r) => normalizeAuthRole(r)),
      };
    }
  }

  return { action: 'allow', userRole };
}

/** Extrai role normalizada do objeto user / fallback user_role no storage. */
export function readRoleFromStorage(storage: {
  getItem: (key: string) => string | null;
}): string {
  try {
    const userStr = storage.getItem('user');
    if (userStr && userStr !== 'undefined' && userStr !== 'null') {
      const userObj = JSON.parse(userStr);
      if (userObj?.role) {
        return normalizeAuthRole(userObj.role);
      }
    }
  } catch {
    // ignore parse errors — fallback abaixo
  }

  const storedRole = storage.getItem('user_role');
  if (storedRole && storedRole !== 'undefined' && storedRole !== 'null') {
    return normalizeAuthRole(storedRole);
  }

  return '';
}
